
import { EventEmitter, Injectable } from '@angular/core';

import { StageConstants } from 'core/constants';
import { CriticalError, TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { FontStoreRequestError, FontStoreService
       } from 'core/fontstore/fontstore.service';
import { Utils } from 'core/utils';
import {first} from 'rxjs/operators';
import { FontDescription, Text } from '../text/text.model';
import { StageService } from './stage/stage.service';

export class FontLoadingError extends TrivialError
{
  // i18n.error.fontLoadingError.title
  // i18n.error.fontLoadingError.message
  protected _name: string = 'FontLoadingError';
}

export class CriticalFontLoadingError extends CriticalError
{
  protected _name: string = 'CriticalFontLoadingError';
}

enum FontLoadingResult {
  Success,
  Failure
}

@Injectable()
export class FontInjectorService
{
  private fonts: Array<FontDescription> = [];
  private loadedFonts: Array<String> = [];

  private onFontListLoaded: EventEmitter<FontLoadingResult> =
      new EventEmitter();

  constructor(
      private stageService: StageService,
      private fontStoreService: FontStoreService,
      private errorService: ErrorService
  )
  {
    this.fontStoreService.getFonts().then((fonts) => {
      this.fonts = fonts;
      this.onFontListLoaded.emit(FontLoadingResult.Success);
      this.onFontListLoaded.complete();
    })
    .catch(async e => {
      this.onFontListLoaded.emit(FontLoadingResult.Failure);
      this.onFontListLoaded.complete();
      await this.buildFontLoadingError(e, 'FontInjectorService', true);
    });
  }

  public async waitForCompleteFontList(): Promise<void>
  {
    if (this.onFontListLoaded.isStopped) {
      return;
    }

    return new Promise<void>((resolve, reject) => {
      this.onFontListLoaded.pipe(first()).subscribe(result => {
        if (result === FontLoadingResult.Success) {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  public getFonts(): Array<FontDescription>
  {
    return this.fonts;
  }

  public getFontVariants(fontName: string): Array<FontDescription>
  {
    const unquotedName =
        fontName.replace(new RegExp(`['"]`, 'g'), '').toLowerCase();

    return this.fonts.filter(f => f.name.toLowerCase() === unquotedName);
  }

  public getFont(fontName: string,
                 bold: boolean = false,
                 italic: boolean = false): FontDescription
  {
    const variants = this.getFontVariants(fontName);

    return Utils.find(variants, (f => f.bold === bold && f.italic === italic))
        || Utils.find(variants, (f => f.bold === bold || f.italic === italic))
        || variants[0];
  }

  /**
   * @throws FontStoreRequestError
   */
  public async addFont(
      fontName: string,
      bold: boolean = false,
      italic: boolean = false,
      timeout: number = undefined): Promise<FontDescription>
  {
    await this.waitForCompleteFontList();

    const font = this.getFont(fontName, bold, italic);

    const fontFace = this.getFontFaceForFont(font);

    const loadedKey = `${font.name}|${font.bold}|${font.italic}`;
    if (this.loadedFonts.indexOf(loadedKey) === -1) {
      await this.fontStoreService.loadFont(font, timeout);
      /**
       * Da der Aufruf von loadFonts asynchron läuft und einige Zeit in Anspruch
       * nehmen kann und wir keine Duplikate haben möchten, ist eine erneute
       * Prüfung erforderlich. Sets werden noch nicht ausreichend unterstützt.
       */
      if (this.loadedFonts.indexOf(loadedKey) === -1) {
        this.loadedFonts.push(loadedKey);
      }
    }
    this.injectFont(fontFace);
    this.addFontToSvg(fontFace);

    /**
     * We must wait until the font is realy applied.
     */
    await (<any>document).fonts.ready;
    return font;
  }

  /**
   * @throws FontStoreRequestError
   */
  public async addModelFont(
      model: Text, timeout?: number): Promise<FontDescription | undefined>
  {
    await this.addFont(
        model.fontName,
        !!model.fontBold,
        !!model.fontItalic,
        timeout);
    return await this.getFontDescription(model);
  }

  public async getFontDescription(
      model: Text): Promise<FontDescription | undefined>
  {
    await this.waitForCompleteFontList();
    return this.getFont(
        model.fontName, !!model.fontBold, !!model.fontItalic);
  }

  private setContainerContent(fontsContainer: Element, content: string): void
  {
    if (fontsContainer.innerHTML === undefined) {
      fontsContainer.textContent = content;
    } else {
      fontsContainer.innerHTML = content;
    }
  }

  private getContainerContent(fontsContainer: Element): string
  {
    return fontsContainer.innerHTML || fontsContainer.textContent;
  }

  private isFontFaceContained(
      fontsContainer: Element, fontFace: string): boolean
  {
    return (fontsContainer.textContent || '').indexOf(fontFace) !== -1;
  }

  private injectFont(fontFace: string): void
  {
    const fontsContainer = this.getFontsContainer();

    if (!this.isFontFaceContained(fontsContainer, fontFace)) {
      this.setContainerContent(fontsContainer,
          '/*<![CDATA[*/' + fontFace + '/*]]>*/\n' +
          this.getContainerContent(fontsContainer));
    }
  }

  private getFontFaceForFont(font: FontDescription): string
  {
    return `@font-face {
      font-family: "${font.name}";
      font-weight: ${font.bold ? 'bold' : 'normal'};
      font-style: ${font.italic ? 'italic' : 'normal'};
      src: url("${font.url}");
    }
    `;
  }

  private getFontsContainer(): Element
  {
    let fontsContainer = document.getElementById('fonts');

    if (!fontsContainer) {
      fontsContainer = document.createElement('style');
      fontsContainer.setAttribute('id', 'fonts');
      document.getElementsByTagName('head')[0].appendChild(fontsContainer);
    }

    return fontsContainer;
  }

  private addFontToSvg(fontFace: string): void
  {
    const stageReference = this.stageService.getCurrentStageReference();
    if (stageReference) {
      const fontsContainer = this.getSvgFontsContainer(stageReference.node);

      if (!this.isFontFaceContained(fontsContainer, fontFace)) {
        this.setContainerContent(fontsContainer,
            '/*<![CDATA[*/' + fontFace + '/*]]>*/\n' +
            this.getContainerContent(fontsContainer));
      }
    }
  }

  private getSvgFontsContainer(elem: Element): Element
  {
    let fontsContainer = elem.querySelector('#fonts');
    if (!fontsContainer) {
      fontsContainer =
          document.createElementNS(StageConstants.XML_NAMESPACE_SVG, 'style');
      fontsContainer.setAttribute('id', 'fonts');
      fontsContainer.setAttribute('data-is-fonts-container', 'yes');
      elem.appendChild(fontsContainer);
    }
    return fontsContainer;
  }

  public async buildFontLoadingError(e: FontStoreRequestError,
      reference: string, critical: boolean = false): Promise<void>
  {
    const message = `[${e.status} ${e.statusText}] ${e.detail}`;
    let error;
    if (critical) {
      error = new CriticalFontLoadingError(message);
    }
    else {
      error = new FontLoadingError(message);
    }
    this.errorService.add(error, reference);
    await this.errorService.flush(reference);
  }
}
